import type { AgentPanelContextType } from '../model/types';
import {
  Building2,
  CalendarCheck,
  CalendarRange,
  ClipboardList,
  Flag,
  Globe,
  Hotel,
  MessageSquare,
  PencilLine,
  Search,
  Sparkles,
  User,
} from 'lucide-react';
import { pickCss } from '@/shared/lib/css-utils';
import { Text } from '@/shared/ui';
import css from './agent-panel-capabilities.module.scss';

const styles = pickCss(css, css.root);

type Capability = {
  title: string;
  example: string;
  icon: React.ReactNode;
};

// Itinerary context — the planner can route any registered command,
// including the plan_trip workflow. Cross-domain actions (sending messages)
// are scoped to /operations and reach itinerary via the navigation-handoff
// card; they're not advertised here. The email-import shortcut used to live
// here; it has been removed.
const ITINERARY_CAPABILITIES: ReadonlyArray<Capability> = [
  {
    title: 'Plan a full trip end to end',
    example: '“Plan a trip to Paris for 2 next week”',
    icon: <Sparkles aria-hidden="true" size={16} />,
  },
  {
    title: 'Check hotel availability',
    example: '“Find a hotel in Oslo June 12–16 for 2”',
    icon: <Hotel aria-hidden="true" size={16} />,
  },
  {
    title: 'Find experiences and restaurants',
    example: '“Find a sushi place in Oslo”',
    icon: <Search aria-hidden="true" size={16} />,
  },
  {
    title: 'Update a product',
    example: '“Update the City Walking Tour price to 45 EUR”',
    icon: <PencilLine aria-hidden="true" size={16} />,
  },
  {
    title: 'Search itinerary items',
    example: '“Show me everything booked for next month”',
    icon: <CalendarCheck aria-hidden="true" size={16} />,
  },
];

// Operations context — `search_operations` applies filters to the URL
// (page-action bridge); `send_whatsapp_message` is cross-domain. The
// flagged/attention bullet stays toolbar-only after the URL migration, so
// we don't list an "operations needing attention" shortcut — the
// consultant clicks the bullet manually.
const OPERATIONS_CAPABILITIES: ReadonlyArray<Capability> = [
  {
    title: 'Find bookings by supplier',
    example: '“Pestana bookings in Lisbon next week”',
    icon: <Building2 aria-hidden="true" size={16} />,
  },
  {
    title: 'Find by traveller or customer',
    example: '“Show bookings for the Smith family”',
    icon: <User aria-hidden="true" size={16} />,
  },
  {
    title: 'Filter by status',
    example: '“Show unconfirmed operations for next week”',
    icon: <CalendarCheck aria-hidden="true" size={16} />,
  },
  {
    title: 'Filter by country or city',
    example: '“Show operations in Portugal in May”',
    icon: <Globe aria-hidden="true" size={16} />,
  },
  {
    title: 'Filter by category',
    example: '“Show all hotel bookings this month”',
    icon: <Hotel aria-hidden="true" size={16} />,
  },
  {
    title: 'Filter by date range',
    example: '“Show operations between June 12 and June 16”',
    icon: <CalendarRange aria-hidden="true" size={16} />,
  },
  {
    title: 'Filter by priority',
    example: '“Show high-priority itinerary operations”',
    icon: <Flag aria-hidden="true" size={16} />,
  },
  {
    title: 'Operations for an itinerary',
    example: '“Show operations for BR0042277”',
    icon: <ClipboardList aria-hidden="true" size={16} />,
  },
  {
    title: 'Send a WhatsApp message',
    example: '“WhatsApp the client tomorrow’s pickup time”',
    icon: <MessageSquare aria-hidden="true" size={16} />,
  },
];

// Products list page context — the agent applies filters to the page URL
// rather than rendering results in-panel (see
// `.claude/rules/agent-panel-page-actions.md`). Each suggestion exercises
// a different mapping branch of `find_product`'s contextType==='products'
// path: category alone, category + place, category + country.
const PRODUCTS_CAPABILITIES: ReadonlyArray<Capability> = [
  {
    title: 'Find hotels in a country',
    example: 'Find me hotels in Portugal',
    icon: <Hotel aria-hidden="true" size={16} />,
  },
  {
    title: 'Find dining in a city',
    example: 'Find sushi in Lisbon',
    icon: <Search aria-hidden="true" size={16} />,
  },
  {
    title: 'Find activities in a place',
    example: 'Show me boat tours in the Algarve',
    icon: <Sparkles aria-hidden="true" size={16} />,
  },
  {
    title: 'Find attractions in a city',
    example: 'Show me museums in Porto',
    icon: <Globe aria-hidden="true" size={16} />,
  },
];

// Itineraries list page context — natural-language filter requests apply
// to the page URL via `query_entity({ entity: 'itineraries' })`'s
// apply_filters branch (the counterpart to PRODUCTS_CAPABILITIES). Each
// example exercises a different mapping branch: travel date, customer,
// owner ("my"), status.
const ITINERARIES_CAPABILITIES: ReadonlyArray<Capability> = [
  {
    title: 'Filter by customer',
    example: 'Show me itineraries for Acme',
    icon: <Building2 aria-hidden="true" size={16} />,
  },
  {
    title: 'My itineraries',
    example: 'Show my itineraries travelling next month',
    icon: <User aria-hidden="true" size={16} />,
  },
  {
    title: 'Filter by reference',
    example: 'Find itinerary IT-001',
    icon: <Search aria-hidden="true" size={16} />,
  },
  {
    title: 'Filter by date range',
    example: 'Show itineraries between June 1 and June 30',
    icon: <CalendarRange aria-hidden="true" size={16} />,
  },
];

const CAPABILITIES_BY_CONTEXT: Readonly<
  Record<AgentPanelContextType, ReadonlyArray<Capability>>
> = {
  itinerary: ITINERARY_CAPABILITIES,
  itineraries: ITINERARIES_CAPABILITIES,
  operations: OPERATIONS_CAPABILITIES,
  products: PRODUCTS_CAPABILITIES,
};

export type AgentPanelCapabilitiesProps = {
  contextType: AgentPanelContextType;
  // Called when the consultant clicks an entry — the host sends the example
  // phrase through command.startSession.
  onPick: (example: string) => void;
};

// Static list of what the agent can do, rendered as the idle empty state.
// The items are clickable shortcuts — clicking one drops the example into
// the planner just like typing it. The list is intentionally hardcoded
// rather than derived from the command registry: the wording is tuned for
// consultants (a friendly label vs the raw command id) and the example
// phrasing has to read naturally. The list varies per
// `contextType` because each domain has a different command catalog.
export const AgentPanelCapabilities = ({
  contextType,
  onPick,
}: AgentPanelCapabilitiesProps) => {
  const capabilities = CAPABILITIES_BY_CONTEXT[contextType];
  return (
    <div className={styles()}>
      <Text className={css.heading} size="xs" variant="muted" weight="semibold">
        What you can ask
      </Text>
      <ul className={css.list}>
        {capabilities.map((cap) => (
          <li key={cap.title}>
            <button
              className={css.item}
              data-agent-focusable=""
              onClick={() => onPick(cap.example)}
              type="button"
            >
              <span className={css.icon}>{cap.icon}</span>
              <span className={css.body}>
                <Text size="sm" weight="medium">
                  {cap.title}
                </Text>
                <Text className={css.example} size="xs" variant="muted">
                  {cap.example}
                </Text>
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};
